import { ApplicationConfig, ErrorHandler, Injectable, Injector, provideZoneChangeDetection, importProvidersFrom, provideAppInitializer, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { provideRouter, withRouterConfig, RouteReuseStrategy } from '@angular/router';
import { withUnsavedWorkGuard } from '@branduals/dialog-guard';
import { installFileDropGuard } from '@branduals/file-drop-guard';
import { CustomRouteReuseStrategy } from './route-reuse.strategy';
import { provideHttpClient, withInterceptorsFromDi, withInterceptors, HttpClient, withXhr } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { providePrimeLocale } from '@branduals/primeng-locale';
import { MessageService, ConfirmationService } from 'primeng/api';
import BrandualsTheme from '@branduals/theme';
import { withCredentialsInterceptor } from './interceptors/with-credentials.interceptor';
import { apiBaseUrlInterceptor } from './interceptors/api-base-url.interceptor';
import { unauthorizedInterceptor } from './interceptors/unauthorized.interceptor';
import { brandNotFoundInterceptor } from './interceptors/brand-not-found.interceptor';
import { provideAuth, LogLevel, AbstractSecurityStorage, authInterceptor } from 'angular-auth-oidc-client';
import { BrandualsAuthStorageService } from '@branduals/auth-storage';
import { routes } from './app.routes';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';
import { Observable } from 'rxjs';
import { AppConfigService } from './services/app-config.service';
import { SystemAccessService } from './services/system-access.service';
import { LocaleService } from './services/locale.service';
import { PreferencesService } from './services/preferences.service';
import { formatAvailableAt } from './utils/available-at.util';
import { classifyUnhandledError, installErrorArgsResolver, installErrorKeyResolver, verdictDetail, type UserError } from '@branduals/graphql-errors';
import { AuthService } from './services/auth.service';
import { publicAsOfSentenceKey } from './utils/public-asof-sentence.utils';
import { quotaRefusalKey } from './utils/quota-refusal-keys';
import { DemoModeError } from './graphql/demo-mode-error';
import { TemporalReadOnlyError } from './graphql/temporal-readonly-error';
import { ConnectivityService } from './services/connectivity.service';
import { PausedRedirectService } from './services/paused-redirect.service';
import { MembershipService, MembershipStanding } from './services/membership.service';
import { ModeAwareMessageService } from './services/mode-aware-message.service';
import { isPlatformHost } from './utils/platform-origin.utils';

// Factory function for the TranslateHttpLoader
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * The trash wall's sentence by the viewer's standing. Every wall that refuses an act on a
 * trashed item (a move, a grant, an attach, a resource add, a review) answers ITEM_IN_TRASH for
 * everyone, but "put it back first" names a door only an admin can open — the Trash view is
 * Admin+. A plain member hears the sentence that names THEIR road instead: My activity for their
 * own deletion, a brand admin otherwise.
 *
 * The answer is by standing ONLY while the standing is known: `loaded` drops to false at the head
 * of every access read, an invalidation refetch included, so it alone would hand a member the
 * admin sentence mid-refetch; the live privilege signal (`isMember`) survives a refetch and is
 * zeroed by a brand switch and by a failed read. Before the first read, through a switch and
 * after a failed read the voice answers nothing and the default admin sentence renders — never
 * the member sentence to an admin.
 */
function trashVoiceKey(systemAccess: SystemAccessService, error: UserError): string | undefined {
  return error.code === 'ITEM_IN_TRASH' && (systemAccess.loaded() || systemAccess.isMember()) && !systemAccess.admin()
    ? 'errors.ITEM_IN_TRASH_MEMBER'
    : undefined;
}

/**
 * The public view's dropped-clock sentence by the reader's standing: the server refuses Time travel to
 * the public view with one code for everyone, and the sentence that names the way back differs — a
 * signed-in reader of a public class already signed in. The key's one home is the pure util the
 * temporal service's own NavigationEnd sentence reads too, so a download, an unhandled error and a
 * failed read carrying that code speak the same words as the clock's drop.
 */
function publicAsOfVoiceKey(auth: AuthService, error: UserError): string | undefined {
  return error.code === 'PUBLIC_ASOF_NOT_AVAILABLE' ? publicAsOfSentenceKey(auth.authenticated()) : undefined;
}

/**
 * The sentences that speak by the viewer's STANDING, and the one that speaks by the refusal's own
 * ARGUMENT, installed together at boot in the shared package's one key seam (a second install would
 * replace the first, so the voices compose here): the trash wall's member sentence, the public view's
 * dropped-clock sentence, and the limit refusal by its quota — the server's one limit code says which
 * limit was reached in its `quota` argument, and the brand app answers a sentence per quota. Every
 * catch site already on the shared helpers speaks them with no site change. Returns the hook's own
 * undo (a test that installs restores the default in its teardown — the unit-test runner shares module
 * state across spec files).
 */
export function installStandingVoices(systemAccess: SystemAccessService, auth: AuthService): () => void {
  return installErrorKeyResolver(error =>
    trashVoiceKey(systemAccess, error) ?? publicAsOfVoiceKey(auth, error) ?? quotaRefusalKey(error),
  );
}

/**
 * The untitled word for a refusal that names a record with no title: the path refusal
 * (`PUBLIC_GRANT_PARENT_NOT_PUBLIC`) names the record an admin must make public first through its
 * `parent` slot, and the server sends that slot EMPTY when the record has no title in any language
 * (its titles read never falls back to English on the wire — the untitled word is the Client's canon,
 * the same word the seat, the marker and the Settings roster say for that record). A named parent and
 * every other code pass through untouched. The locale service resolves LAZILY at the refusal through
 * the injector, never at boot: its construction reads the tenant config the config initializer is still
 * loading (the file-drop voice's shape, for the same reason).
 */
function untitledRecordArgs(injector: Injector, error: UserError): Record<string, string> | undefined {
  return error.code === 'PUBLIC_GRANT_PARENT_NOT_PUBLIC' && !error.args?.['parent']?.trim()
    ? { ...error.args, parent: injector.get(LocaleService).untitledFallback(null) }
    : undefined;
}

/** The refusals of Settings → Languages whose sentence names a CONTENT language through its `locale` slot. */
const CONTENT_LANGUAGE_REFUSALS: ReadonlySet<string> = new Set([
  'LOCALE_NOT_SUPPORTED', 'LOCALE_ALREADY_EXISTS', 'LOCALE_NOT_FOUND',
]);
/** Their twins for a LABEL language. */
const LABEL_LANGUAGE_REFUSALS: ReadonlySet<string> = new Set([
  'OPERATIONAL_LANGUAGE_NOT_SUPPORTED', 'OPERATIONAL_LANGUAGE_ALREADY_EXISTS', 'OPERATIONAL_LANGUAGE_NOT_FOUND',
]);

/**
 * The language's NAME for a refusal that names a language: the server feeds the `locale` slot of the six
 * language refusals a language CODE (or several, joined by a comma and a space), and a member never reads a
 * code where a language is meant — "de-DE" says nothing in any of the interface languages. The one namer
 * fills the slot in the reader's interface language, each code named among the brand's own set of the
 * refusal's family (content languages or label languages) beside the slot's codes — the rule the
 * content-language page labels its own list by, so a variant shows its region only where a sibling of the
 * same language stands beside it (the label-language page's rows read an endonym beside an English name:
 * that list's register, not a refusal's). A list stays a list, in the wire's order: it is split on the
 * comma ALONE, because the namer handed a whole list as one code would answer its first language and drop
 * the rest without a word. An empty or absent slot answers nothing (there is nothing to name), a code the
 * namer cannot name reads as the code, and a refusal outside the six keeps its args whatever they carry.
 * The locale service resolves lazily, as for the untitled word.
 */
function languageNameArgs(injector: Injector, error: UserError): Record<string, string> | undefined {
  const contentFamily = CONTENT_LANGUAGE_REFUSALS.has(error.code);
  if (!contentFamily && !LABEL_LANGUAGE_REFUSALS.has(error.code)) return undefined;
  const codes = (error.args?.['locale'] ?? '').split(',').map(code => code.trim()).filter(code => code.length > 0);
  if (codes.length === 0) return undefined;
  const locale = injector.get(LocaleService);
  const among = [...(contentFamily ? locale.getSupportedContentLanguages() : locale.getBrandOperationalLanguages()), ...codes];
  return { ...error.args, locale: codes.map(code => locale.languageName(code, { among })).join(', ') };
}

/**
 * The date with its clock for a refusal that says WHEN something is available again: the server states the next
 * allowed instant under the `availableAt` slot as a raw ISO string (the Insights spacing — a report of one type
 * asked for again before its plan's spacing has passed), and a member never reads a raw instant. The `date` slot
 * is fed from it in the ONE shape every "available again" sentence speaks (`formatAvailableAt`: the date home, with
 * the clock — on a plan whose spacing is a day, a date alone would read as "today" — in the reader's interface
 * language and the member's stored zone), the same shape the Insights page's doors read the schedule through, so
 * the toast and the shut door behind it never spell the moment two ways. The voice keys on the slot, not on a code:
 * every refusal that carries such an instant reads the same way. Both services resolve lazily at the refusal, as
 * the other voices do.
 */
function availableAtArgs(injector: Injector, error: UserError): Record<string, string> | undefined {
  const availableAt = error.args?.['availableAt'];
  if (!availableAt) return undefined;
  const language = injector.get(LocaleService).activeUILanguage();
  const zone = injector.get(PreferencesService).preferences().timeZone || 'UTC';
  return { ...error.args, date: formatAvailableAt(availableAt, language, zone) };
}

/**
 * The slots the Client fills from its OWN canon, installed together at boot in the shared package's one
 * args seam (a second install would replace the first, so the voices compose here, as the standing voices
 * do in the key seam): the untitled word for a record with no title, the language's name for a refusal
 * that names a language, and the date with its clock for a refusal that says when something is available
 * again. Every refusal face and every door on `resolveErrorMessage` / `caughtErrorDetail` speaks the filled
 * sentence with no site change; an error no voice answers keeps the wire's own args. Returns the hook's own undo.
 */
export function installSlotVoices(injector: Injector): () => void {
  return installErrorArgsResolver(error =>
    untitledRecordArgs(injector, error) ?? languageNameArgs(injector, error) ?? availableAtArgs(injector, error));
}

/**
 * The voice of the shell's file-drop guard: an OS file released anywhere no upload zone claimed — the
 * sidebar, the topbar, an admin table, the dashboard — is caught (the tab never navigates away and the
 * brand app never unloads with its in-flight work) and the road is named. A public brand's visitor has no
 * upload road at all (every zone stands down in public mode), so they hear the visitor's own sentence
 * instead of an instruction to upload; the navigation is prevented for every principal alike. The toast
 * bus, the catalog and the admission signal are resolved at DROP time through the injector, never at
 * boot: the mode-aware MessageService (THE MessageService on every brand, `ModeAwareMessageService`) must
 * not be resolved before the tenant config lands (the error handler below says why). Returns the guard's
 * uninstaller.
 */
export function installFileDropVoice(injector: Injector, doc: Document): () => void {
  return installFileDropGuard(doc, () => {
    const sentenceKey = injector.get(MembershipService).publicMode() ? 'upload.dropAsVisitor' : 'upload.dropOutsideZone';
    injector.get(MessageService).add({
      severity: 'info',
      summary: injector.get(TranslateService).instant(sentenceKey),
    });
  });
}

/**
 * Global error handler that catches unhandled errors and shows toast notifications.
 *
 * Only fires for truly unhandled errors (uncaught in change detection, event handlers,
 * or unsubscribed Observable errors). Errors caught by component try/catch blocks
 * never reach this handler. The classify-then-speak core is the shared package's
 * (`classifyUnhandledError` + `verdictDetail` — the promise unwrap, the OIDC suppressions,
 * the network predicate, the coded/mutation/generic split); this class keeps only what
 * is the Client's own: the two typed mode-error skips and the paused-member suppression leg,
 * and the wiring of the network verdict to this app's connectivity bar.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  // ErrorHandler is instantiated EAGERLY during platform bootstrap, before
  // APP_INITIALIZER runs. Resolving MessageService here directly would resolve
  // the mode-aware provider (`ModeAwareMessageService`, THE MessageService on
  // every brand) before the tenant config lands — on a demo brand, isDemo()
  // would still be false (activate() runs inside the APP_INITIALIZER HTTP tap),
  // so the mode suppression would miss its first window. Lazy-resolve at
  // handleError time.
  private readonly _injector = inject(Injector);
  private _messageService?: MessageService;
  private get messageService(): MessageService {
    return this._messageService ??= this._injector.get(MessageService);
  }
  private readonly translateService = inject(TranslateService);
  private readonly connectivity = inject(ConnectivityService);

  // Both legs lazy-resolved (this handler is eager-bootstrap — see the MessageService note).
  private pausedSuppressionOpen(): boolean {
    return this._injector.get(PausedRedirectService).suppressionOpen();
  }

  private membershipStanding(): MembershipStanding {
    return this._injector.get(MembershipService).membershipState().standing;
  }

  private publicMode(): boolean {
    return this._injector.get(MembershipService).publicMode();
  }

  handleError(error: unknown): void {
    const verdict = classifyUnhandledError(error);

    // Skip the two typed mode errors (each already handled with its own toast): the demo-mode
    // refusal and its structural twin, the time-travel read-only refusal — a second red toast
    // over either would double-message, and the mode-aware MessageService's suppression window
    // must not be the only thing standing between the member and that duplicate.
    if (verdict.unwrapped instanceof DemoModeError || verdict.unwrapped instanceof TemporalReadOnlyError) return;

    // The OIDC token-refresh and stale-callback failures — the library and the callback
    // component already recover; nothing useful to say here.
    if (verdict.kind === 'suppress') return;

    // Network errors (connection refused, timeout, DNS failure) —
    // delegate to connectivity bar instead of showing misleading error toasts.
    if (verdict.kind === 'network') {
      console.warn('Network error detected, triggering connectivity check:', verdict.unwrapped);
      this.connectivity.checkNow();
      return; // Connectivity bar will handle user communication
    }

    console.error('Unhandled error:', verdict.unwrapped);

    // Access-denial suppression for a paused member: while the paused re-check is in
    // flight, OR once the standing IS paused (stragglers from slower parallel queries
    // land after the window closed), the denial toast is withheld — the access-paused
    // wall is the one honest message, and a scolding toast on top of it double-messages.
    // A non-paused resolution re-dispatches the held denial back through here with the
    // window closed, so nothing silently vanishes for anyone else.
    if (verdict.kind === 'coded') {
      const codes = verdict.entries.map(e => e.code);
      if (codes.includes('AUTH_NOT_AUTHORIZED') && (this.pausedSuppressionOpen() || this.membershipStanding() === 'paused')) {
        return;
      }
      // In public mode BOTH refusal codes are withheld: a public brand's visitor whose brand just turned its
      // public access off meets a refusal on every read — the marked roots' answer, whichever code the
      // server chose — and the config re-read owns what happens next; a toast over the page would
      // double-message the very state that re-read resolves.
      if ((codes.includes('AUTH_NOT_AUTHORIZED') || codes.includes('AUTH_NOT_AUTHENTICATED')) && this.publicMode()) {
        return;
      }
    }

    const t = (key: string, params?: Record<string, unknown>) =>
      this.translateService.instant(key, params) as string;

    // A platform refusal (rate rail, cost budget, batch shape) threads its identity as
    // toast data so the mode-aware MessageService coalesces a burst into ONE toast per
    // code per window — the floor scenario is every in-flight query failing at once.
    this.messageService.add({
      severity: 'error',
      summary: t('common.error'),
      detail: verdictDetail(verdict, t, 'common.errorGeneric'),
      life: 5000,
      ...(verdict.refusal ? { data: verdict.refusal } : {})
    });
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      function initializerFn(): Observable<unknown> {
        const appConfigService = inject(AppConfigService);

        return appConfigService.loadConfig('api');
      }

      return initializerFn();
    }),
    // The two voices by standing — the trash wall's and the public view's dropped clock — installed before
    // the first render, so no catch site can speak the admin sentence to a member or "sign in" to a reader
    // who already did (the access service only subscribes here; its read starts later).
    provideAppInitializer(() => {
      installStandingVoices(inject(SystemAccessService), inject(AuthService));
    }),
    // The slots the Client fills from its own canon, installed beside the standing voices (the trash voice
    // and the asOf refusal's, one install above): the untitled word fills the empty title of a refusal that
    // names a record, and the six language refusals read the language's name where the wire carried its
    // code. The locale service is resolved at the refusal, not here.
    provideAppInitializer(() => {
      installSlotVoices(inject(Injector));
    }),
    // The shell's file-drop guard: an OS file released outside every upload zone never navigates the
    // tab away, and the release is refused out loud with the road named.
    provideAppInitializer(() => {
      installFileDropVoice(inject(Injector), inject(DOCUMENT));
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // The derived unsaved-work guard wraps the WHOLE table (new routes inherit it), and
    // 'computed' keeps history honest on a guard-refused Back: the default 'replace' would
    // overwrite the entry the member navigated back to, destroying the page they came from.
    provideRouter(withUnsavedWorkGuard(routes), withRouterConfig({ canceledNavigationResolution: 'computed' })),
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    provideHttpClient(withXhr(), 
      withInterceptorsFromDi(),
      // Auth interceptor must run BEFORE apiBaseUrlInterceptor so secureRoutes matching works
      // unauthorizedInterceptor catches 401s and triggers token refresh / re-login
      // brandNotFoundInterceptor feeds the open tab's move when this host stops serving its brand
      withInterceptors([unauthorizedInterceptor, brandNotFoundInterceptor, authInterceptor(), apiBaseUrlInterceptor, withCredentialsInterceptor])
    ),
    providePrimeNG({
      theme: {
        preset: BrandualsTheme,
        options: {
          darkModeSelector: '[data-theme="dark"]'
        }
      },
      ripple: true
    }),
    // PrimeNG's own words, its calendar's names and week start, and the shape of a typed date follow the
    // interface language: the shared feed hands them over whenever a language's catalog lands, the boot one
    // included (the library ships English for all of it, and showed it to every reader).
    providePrimeLocale(),
    provideAuth({
      config: {
        authority: environment.authServerUrl,
        authWellknownEndpointUrl: environment.authServerUrl,
        redirectUrl: window.location.protocol + '//' + location.host + '/auth/signin-oidc',
        postLogoutRedirectUri: environment.authServerUrl + '/auth/signout-callback',
        clientId: 'brand_client_id',
        scope: 'openid profile email offline_access',
        responseType: 'code',
        silentRenew: true,
        useRefreshToken: true,
        // Serialize refresh-token grants across tabs (Web Locks): several tabs waking together
        // each see their socket refused and would otherwise POST the same rolling refresh token
        // in parallel, and any grant landing past the server's reuse leeway fails with
        // invalid_grant and signs that tab out. The lock also short-circuits a tab whose sibling
        // already refreshed.
        useRefreshTokenLock: true,
        logLevel: LogLevel.Warn,
        autoUserInfo: false,
        autoCleanStateAfterAuthentication: true, // Enable cleanup after auth
        disableIdTokenValidation: false,
        historyCleanupOff: false, // Enable cleanup
        tokenRefreshInSeconds: 60, // Reduce storage writes (default is 3)
        // '/api' and '/graphql' match relative paths before apiBaseUrlInterceptor rewrites to API server.
        // Auth server API path added explicitly — only matches /api/* (not OIDC discovery endpoints).
        secureRoutes: ['/api', '/graphql', `${environment.authServerUrl}/api`],
        ignoreNonceAfterRefresh: true,      // prevent authCallback incorrect nonce
        renewTimeBeforeTokenExpiresInSeconds: 60,
        // Session monitoring (library default interval) on platform hosts only. A custom domain
        // cannot run it: the auth server's frame-ancestors policy admits only platform origins,
        // and even a permitted frame would read a partitioned third-party session cookie and
        // report a change on every poll. There, a sign-out elsewhere surfaces at the next token
        // refresh or cold boot.
        startCheckSession: isPlatformHost(window.location.hostname),
        triggerAuthorizationResultEvent: true // Enable automatic token exchange
      }
    }),
    { provide: AbstractSecurityStorage, useClass: BrandualsAuthStorageService },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // ALWAYS installed: Time travel is a runtime-entered,
    // all-brands mode — a bootstrap-time isDemo() gate cannot express it, so
    // the suppression-aware MessageService serves every brand and consults
    // BOTH mode guards lazily (demo + temporal). Formerly demo-brands-only.
    ModeAwareMessageService,
    { provide: MessageService, useExisting: ModeAwareMessageService },
    ConfirmationService,
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en-US',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    )
  ]
};
